import React, { useMemo } from "react";
import ComponentCentered from "./component-modes/ComponentCentered";
import ComponentSplit from "./component-modes/ComponentSplit";
import ComponentAlternating from "./component-modes/ComponentAlternating";
import ComponentMosaic from "./component-modes/ComponentMosaic";
import ComponentGallery from "./component-modes/ComponentGallery";
import {
  sortChildElements,
  AUTO_LAYOUT_MEDIA_NO_MARGIN,
} from "./component-modes/componentModeUtils";

/**
 * Component automatic-mode dispatcher.
 *
 * Maps component_mode IDs to dedicated React renderers. Each renderer owns
 * structural layout (how images interact with text). The layout — not the item —
 * owns the spacing between media, so every media element's own margin is forced
 * to 0 here (`AUTO_LAYOUT_MEDIA_NO_MARGIN`); item-level PADDING is left intact.
 *
 * Falls back to ComponentCentered for unknown modes.
 */

const MODE_COMPONENTS = {
  // Centered family
  centered: ComponentCentered,
  centered_text_only: ComponentCentered,
  centered_full: ComponentCentered,
  centered_carousel: ComponentCentered,

  // Split family
  split_left: ComponentSplit,
  split_right: ComponentSplit,
  split_left_full: ComponentSplit,
  split_right_full: ComponentSplit,
  split_left_carousel: ComponentSplit,
  split_right_carousel: ComponentSplit,

  // Alternating family
  alternating: ComponentAlternating,
  alternating_right: ComponentAlternating,
  alternating_full: ComponentAlternating,
  alternating_right_full: ComponentAlternating,
  alternating_carousel: ComponentAlternating,
  alternating_right_carousel: ComponentAlternating,

  // Mosaic family
  mosaic: ComponentMosaic,
  mosaic_right: ComponentMosaic,
  mosaic_full: ComponentMosaic,
  mosaic_right_full: ComponentMosaic,
  mosaic_shuffle: ComponentMosaic,
  mosaic_right_shuffle: ComponentMosaic,
  mosaic_drift: ComponentMosaic,
  mosaic_right_drift: ComponentMosaic,

  // Gallery family
  gallery: ComponentGallery,
  gallery_masonry: ComponentGallery,
  gallery_full: ComponentGallery,
  gallery_carousel: ComponentGallery,
  gallery_drift: ComponentGallery,
};

const ComponentAutomatic = ({ component, containerWidth, breakpoint }) => {
  const modeId = component?.component_mode;
  const modeDefinition = component?.active_component_mode_definition || null;
  const ModeComponent = MODE_COMPONENTS[modeId] || ComponentCentered;

  const elements = useMemo(() => {
    const childItems = component?.all_child_components_and_items || [];
    return sortChildElements(childItems);
  }, [component?.all_child_components_and_items]);

  return (
    // ``data-auto-add-host`` is the hover/focus host for the edit-mode "Add
    // <type>" placeholders (componentEditAffordances.css). Hovering anywhere in
    // the component reveals + expands them; harmless outside edit mode where no
    // placeholder is rendered.
    <div className={AUTO_LAYOUT_MEDIA_NO_MARGIN} data-auto-add-host="true">
      <ModeComponent
        component={component}
        elements={elements}
        modeDefinition={modeDefinition}
        breakpoint={breakpoint}
        containerWidth={containerWidth}
      />
    </div>
  );
};

export default React.memo(ComponentAutomatic);
